import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

export interface TimeSlot {
  time: string;  // HH:MM format
  available: boolean;
}

export interface SlotSelectorProps {
  availableSlots: Record<string, TimeSlot[]>;  // Date (YYYY-MM-DD) -> TimeSlots
  selectedDate: string | null;
  selectedTime: string | null;
  sessionType: 'INTAKE' | 'REGULAR' | 'CRISIS';
  onDateSelect: (date: string) => void;
  onTimeSelect: (time: string) => void;
  onSessionTypeChange: (type: 'INTAKE' | 'REGULAR' | 'CRISIS') => void;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * SlotSelector component for booking wizard
 * Displays a calendar for date selection and time slots for the selected date
 */
export function SlotSelector({
  availableSlots,
  selectedDate,
  selectedTime,
  sessionType,
  onDateSelect,
  onTimeSelect,
  onSessionTypeChange,
  minDate = new Date(),
  maxDate,
}: SlotSelectorProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get days in current month view
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty slots for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const days = getDaysInMonth();

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const isDateAvailable = (date: Date): boolean => {
    const dateStr = formatDate(date);
    const slots = availableSlots[dateStr];
    return slots && slots.some((slot) => slot.available);
  };

  const isDateSelectable = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) return false;
    if (minDate && date < minDate) return false;
    if (maxDate && date > maxDate) return false;

    return isDateAvailable(date);
  };

  const selectedDateSlots = selectedDate ? (availableSlots[selectedDate] || []) : [];

  return (
    <div className="space-y-6">
      {/* Session Type Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Session Type
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => onSessionTypeChange('INTAKE')}
            className={clsx(
              'px-4 py-3 text-sm font-medium rounded-lg border-2 transition-colors',
              sessionType === 'INTAKE'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            )}
          >
            Initial Consultation
          </button>
          <button
            type="button"
            onClick={() => onSessionTypeChange('REGULAR')}
            className={clsx(
              'px-4 py-3 text-sm font-medium rounded-lg border-2 transition-colors',
              sessionType === 'REGULAR'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            )}
          >
            Regular Session
          </button>
          <button
            type="button"
            onClick={() => onSessionTypeChange('CRISIS')}
            className={clsx(
              'px-4 py-3 text-sm font-medium rounded-lg border-2 transition-colors',
              sessionType === 'CRISIS'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            )}
          >
            Crisis Session
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Next month"
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="py-2 text-center text-xs font-semibold text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7">
            {days.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const dateStr = formatDate(date);
              const isSelected = dateStr === selectedDate;
              const isSelectable = isDateSelectable(date);
              const isToday =
                date.toDateString() === new Date().toDateString();

              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => isSelectable && onDateSelect(dateStr)}
                  disabled={!isSelectable}
                  className={clsx(
                    'aspect-square p-2 text-sm border-r border-b border-gray-100 transition-colors',
                    isSelected && 'bg-indigo-600 text-white font-semibold',
                    !isSelected && isToday && 'bg-indigo-50 text-indigo-600 font-semibold',
                    !isSelected && !isToday && isSelectable && 'hover:bg-gray-50 text-gray-900',
                    !isSelectable && 'text-gray-300 cursor-not-allowed'
                  )}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Time Slots */}
      {selectedDate && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Available Times for {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </label>

          {selectedDateSlots.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              No available time slots for this date
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {selectedDateSlots.map((slot) => (
                <button
                  key={slot.time}
                  type="button"
                  onClick={() => slot.available && onTimeSelect(slot.time)}
                  disabled={!slot.available}
                  className={clsx(
                    'px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors',
                    selectedTime === slot.time
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : slot.available
                      ? 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                  )}
                >
                  {new Date(`2000-01-01T${slot.time}`).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
